// Shared strategy position-transition helpers for the Market Health charts.
//
// Both StrategyChart (green/red equity-line zones) and ScoreChart (buy/sell
// triangles on the S&P line) need to know, per strategy and per lag setting,
// exactly which days the strategy flipped between 'invested' and 'cash'. This
// module owns that derivation so the two charts don't duplicate it.

import { selectLag } from './strategyLagSelect';

// Maps the `mode` prop to the per-entry history field carrying that mode's
// equity/position/signal (v4: 2 active modes — adaptive, conservative).
export const MODE_HISTORY_FIELD = {
  adaptiveBalanced: 'strategyAdaptiveBalanced',
  balanced: 'strategyBalanced',
  adaptive: 'strategyAdaptive',
  conservative: 'strategyConservative',
};

// Reads a sorted point's position ('invested'|'cash'|undefined) for the given
// mode field, honoring the 1-day-lag toggle.
const positionAt = (point, modeField, lag) =>
  selectLag(point?.[modeField], lag)?.position;

/**
 * Collects the strategy's trade days from a sorted (oldest→newest) point list:
 * every day the position flips. Returns `[{ ts, type }]` where `type` is:
 *   - 'buy'  — cash → invested (re-entry)
 *   - 'sell' — invested → cash (exit)
 *
 * The FIRST point is not a trade (there's no prior position to flip from) —
 * only genuine transitions between consecutive points count. Points whose
 * position is undefined (missing mode field) break the run without emitting a
 * marker, so a data gap can't manufacture a phantom trade. `modeField` comes
 * from MODE_HISTORY_FIELD[mode]; `lag` is the execution-lag toggle. Returns []
 * for empty input or an unknown mode field.
 */
export const collectPositionTransitions = (points, modeField, lag) => {
  const list = points || [];
  if (list.length === 0 || !modeField) return [];

  const transitions = [];
  let prev = positionAt(list[0], modeField, lag);
  for (let i = 1; i < list.length; i++) {
    const position = positionAt(list[i], modeField, lag);
    if (position && prev && position !== prev) {
      transitions.push({
        ts: list[i].ts,
        type: position === 'invested' ? 'buy' : 'sell',
      });
    }
    if (position) prev = position;
  }
  return transitions;
};
